import type { MonsterSnapshot, NetObjectId, ObjectSnapshot } from '../net/protocol.js';
import { PLAYER_RADIUS_M } from '../sim/positioning.js';
import { worldToScreen, type Camera, type Point, type Viewport } from './input.js';

export interface RangeRing { readonly radiusM: number; readonly label: string; }
export interface ChargeWarning {
  readonly phase: 'WINDUP' | 'DASH';
  readonly direction: Point;
  readonly lengthM: number;
  /** Swept monster impact radius plus the player collision radius. */
  readonly radiusM: number;
}
export interface MonsterOverlayGeometry {
  readonly rings: readonly RangeRing[];
  readonly warning: ChargeWarning | null;
}

function positive(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value) && value > 0;
}

/** Selection is a visual affordance, not an authoritative targeting request. */
export function selectMonsterAtPoint(objects: readonly ObjectSnapshot[], point: Point): MonsterSnapshot | undefined {
  return objects.filter((object): object is MonsterSnapshot => object.kind === 'monster' && object.alive)
    .map((object) => ({ object, distance: Math.hypot(object.x - point.x, object.y - point.y) }))
    .filter(({ object, distance }) => distance <= Math.max(0.5, object.attackPresentation?.hitboxRadiusM ?? 0.5))
    .sort((a, b) => a.distance - b.distance || a.object.netId.localeCompare(b.object.netId))[0]?.object;
}

/** All values come from replicated server facts; no browser attack calculation. */
export function monsterOverlayGeometry(monster: MonsterSnapshot, localPlayerNetId: NetObjectId): MonsterOverlayGeometry {
  const range = monster.attackPresentation;
  const rings: RangeRing[] = [];
  if (!monster.alive || !range) return { rings, warning: null };
  if (positive(range.meleeRangeM)) rings.push({ radiusM: range.meleeRangeM, label: '근접 공격' });
  if (positive(range.projectileRangeM)) rings.push({ radiusM: range.projectileRangeM, label: '투사체 발사' });
  const telegraph = monster.telegraph;
  if (!range.charge || !telegraph || telegraph.targetNetId !== localPlayerNetId || telegraph.phase === 'RECOVERY') return { rings, warning: null };
  const toward = telegraph.direction ?? (telegraph.targetPoint
    ? { x: telegraph.targetPoint.x - monster.x, y: telegraph.targetPoint.y - monster.y } : null);
  const norm = toward ? Math.hypot(toward.x, toward.y) : 0;
  if (!toward || norm < 1e-9) return { rings, warning: null };
  const lengthM = telegraph.phase === 'DASH'
    ? telegraph.remainingDistanceM ?? 0 : range.charge.maxDistanceM;
  if (!positive(lengthM)) return { rings, warning: null };
  return { rings, warning: {
    phase: telegraph.phase, direction: { x: toward.x / norm, y: toward.y / norm }, lengthM,
    radiusM: (telegraph.impactRadiusM ?? range.charge.impactRadiusM) + PLAYER_RADIUS_M,
  } };
}

export function overlayRadiusPixels(radiusM: number, pixelsPerMetre: number): number {
  return radiusM * pixelsPerMetre;
}

/** Narrow drawing interface to keep this module compilable in non-DOM tests. */
export interface MonsterOverlayCanvas {
  save(): void;
  restore(): void;
  beginPath(): void;
  arc(x: number, y: number, radius: number, startAngle: number, endAngle: number): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  setLineDash(segments: number[]): void;
  fillText(text: string, x: number, y: number): void;
  strokeStyle: unknown;
  fillStyle: unknown;
  lineWidth: number;
  lineCap: string;
  font: string;
  textAlign: string;
}

export function renderMonsterRangeOverlay(
  ctx: MonsterOverlayCanvas, monster: MonsterSnapshot | undefined, localPlayerNetId: NetObjectId | undefined,
  camera: Camera, viewport: Viewport,
): void {
  if (!monster || !localPlayerNetId) return;
  const overlay = monsterOverlayGeometry(monster, localPlayerNetId);
  if (overlay.rings.length === 0 && !overlay.warning) return;
  const origin = worldToScreen(monster, camera, viewport);
  const scale = camera.pixelsPerMetre;
  ctx.save();
  ctx.font = '12px system-ui';
  ctx.textAlign = 'center';
  for (const ring of overlay.rings) {
    const projectile = ring.label === '투사체 발사';
    const radius = overlayRadiusPixels(ring.radiusM, scale);
    ctx.beginPath();
    ctx.setLineDash(projectile ? [5, 5] : []);
    ctx.arc(origin.x, origin.y, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = projectile ? 'rgba(255, 196, 103, .88)' : 'rgba(255, 128, 128, .88)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = projectile ? '#ffd59d' : '#ffc6c6';
    ctx.fillText(`${ring.label} ${ring.radiusM.toFixed(1)}m`, origin.x, origin.y - radius - 7);
  }
  if (overlay.warning) {
    const warning = overlay.warning;
    const end = { x: origin.x + warning.direction.x * warning.lengthM * scale, y: origin.y + warning.direction.y * warning.lengthM * scale };
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(origin.x, origin.y); ctx.lineTo(end.x, end.y);
    ctx.strokeStyle = 'rgba(255, 85, 85, .22)';
    ctx.lineWidth = warning.radiusM * 2 * scale;
    ctx.lineCap = 'round'; ctx.stroke();
    ctx.beginPath(); ctx.moveTo(origin.x, origin.y); ctx.lineTo(end.x, end.y);
    ctx.setLineDash([7, 5]); ctx.strokeStyle = 'rgba(255, 90, 90, .95)'; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = '#ffb3a7';
    ctx.fillText(warning.phase === 'WINDUP' ? '돌진 예고' : '돌진 중', end.x, end.y - warning.radiusM * scale - 8);
  }
  ctx.restore();
}
